import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import { formatDate } from "../../../../common/formatDate";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import Checkbox from "@material-ui/core/Checkbox";

const useStyles = makeStyles({
  titleBar: {
    minHeight: 56,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    rowGap: 8,
    columnGap: 8,
    padding: "8px 5px",
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },


  titleLeft: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: 8,
    minWidth: 0,
  },

  backBtn: {
    display: "flex",
    alignItems: "center",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#4b5563",
    fontSize: 12,
    "&:hover": {
      color: "#1976d2",
    },
  },

  divider: {
    width: 1,
    height: 24,
    background: "#ddd",
    margin: "0 16px",
  },

  documentTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  docId: {
    color: "#6b7280",
    fontSize: 14,
  },

  title: {
    fontSize: 14,
    fontWeight: 500,
    color: "#111827",
  },

  titleRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    rowGap: 8,
  },

  status: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 20,
    background: "#eef4ff",
    color: "#2563eb",
    fontSize: 10,
    fontWeight: 600,
  },

  statusDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#2563eb",
  },

  version: {
    color: "#6b7280",
    fontSize: 14,
  },

  submitBtn: {
    height: 36,
    minWidth: 90,
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    cursor: "pointer",
    background: "#126B42",
    color: "#fff",
    fontSize: 14,
    fontWeight: 500,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    "&:hover": {
      background: "#0d5c38",
    },
  },
  drawerPaper: {
    width: 350,
    padding: 20,
  },

  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },

  versionCard: {
    background: "#edf7f4",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    cursor: "pointer",
  },

  badge: {
    display: "inline-block",
    marginTop: 8,
    padding: "3px 8px",
    borderRadius: 4,
    background: "#eef4ff",
    color: "#2563eb",
    fontSize: 9,
  },
  userList: {
    display: "flex",
    alignItems: "center",
    marginLeft: 8,
  },

  avatar: {
    width: 25,
    height: 25,
    fontSize: 8,
    fontWeight: 600,
    border: "2px solid #fff",
    marginLeft: -8,
  },

  onlineDot: {
    backgroundColor: "#22c55e",
    color: "#22c55e",
    boxShadow: "0 0 0 2px white",
  },

  onlineText: {
    marginLeft: 10,
    color: "#6b7280",
    fontSize: 12,
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "calc(100vh - 80px)",
    color: "#6b7280",
    fontSize: 14,
  },
  drawerContent: {
    flex: 1,
    overflowY: "auto",
    padding: 16,

    "&::-webkit-scrollbar": {
      width: 6,
    },

    "&::-webkit-scrollbar-track": {
      background: "#F3F4F6",
    },

    "&::-webkit-scrollbar-thumb": {
      background: "#C7CDD4",
      borderRadius: 20,
    },
  },
  versionItem: {
    padding: "16px 0",
    borderBottom: "1px solid #F1F3F5",

    "&:last-child": {
      borderBottom: "none",
    },
  },

  versionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 4,
  },

  subText: {
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 4,
  },

  date: {
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 10,
  },
  drawerFooter: {
    padding: 16,
    borderTop: "1px solid #E5E7EB",
    background: "#fff",
  },
});

interface ReportImage {
  blob_url: string;
  image_name: string;
}

interface ReportImageGroup {
  [category: string]: ReportImage[];
}

// Updated interface to include project_id and bom_workorder
export interface SelectedImage {
  serial_number?: string;
  project_id?: number | string;
  bom_workorder?: string;
  images: any[];
  type: "blob" | "base64";
}

interface TipTapTitleBarProps {
  title?: string;
  version?: string;
  status?: string;
  versionHistory?: any[];
  onBack?: () => void;
  onSubmit?: () => void;
  workOrder?: string;
  ProjectId?: string;
  reportImages?: any[];
  selectedImages?: SelectedImage[];
  onImageSelect?: (image: SelectedImage) => void;
  onInsertImages?: (position?: number | null) => void;
  isLoading?: boolean;
}

export const TipTapTitleBar = ({
  title = "Sample Report Document",
  version = "v3",
  status = "Writing",
  versionHistory = [],
  onBack,
  workOrder,
  ProjectId,
  onSubmit,
  reportImages = [],
  selectedImages = [],
  onImageSelect = () => { },
  onInsertImages = () => { },
  isLoading = false,
}: TipTapTitleBarProps) => {
  const classes = useStyles();

  const onlineUsers = [
    {
      id: 1,
      initials: "CG",
      color: "#0f766e",
    },
    {
      id: 2,
      initials: "R",
      color: "#6d28d9",
    },
  ];

  const [historyOpen, setHistoryOpen] = useState(false);
  const [IntakeImagesOpen, setIntakeImagesOpen] = useState(false);

  const [imageSource, setImageSource] = useState<"library" | "lsdb">("library");

  const [elImages, setElImages] = useState<any[]>([]);
  const [insertPosition, setInsertPosition] = useState<number | null>(null);

  useEffect(() => {
    const handleOpenImageDrawer = (event: any) => {
      const { is_image, images, insertPosition } = event.detail || {};

      if (!is_image) return;
      setImageSource("lsdb");
      setElImages(images || []);

      setInsertPosition(insertPosition || null);

      setIntakeImagesOpen(true);
    };

    window.addEventListener("open-image-drawer", handleOpenImageDrawer);
    return () =>
      window.removeEventListener("open-image-drawer", handleOpenImageDrawer);
  }, []);

  return (
    <>
      <div className={classes.titleBar}>
        <div className={classes.titleLeft}>
          <button className={classes.backBtn} onClick={onBack}>
            <KeyboardBackspaceIcon style={{ fontSize: 15, marginRight: 4 }} />
            <span>Back to Templates</span>
          </button>

          <div className={classes.divider} />

          <div className={classes.documentTitle}>
            <span className={classes.title}>R{ProjectId}</span>
          </div>
        </div>

        <div className={classes.titleRight}>

          {/* <button className={classes.submitBtn} onClick={onSubmit}>
            Submit for review
          </button> */}
        </div>
      </div>
    </>
  );
};
